/* global BigInt */
// Preserve the API's six-decimal quantities/prices when calculating invoice bases.
// Edited unit prices use the form's four decimals. Convert only at the boundary.
const SCALE = BigInt(10000);
const HUNDRED = BigInt(100) * SCALE;

function fixed(value, decimals = 4) {
  const text = String(value ?? '').trim().replace(',', '.');
  if (!text || !Number.isFinite(Number(text))) throw new Error('Valoare numerică invalidă.');
  const number = Number(text);
  if (Math.abs(number) > 1e10) throw new Error('Valoarea este prea mare.');
  return BigInt(number.toFixed(decimals).replace('.', ''));
}

// Decimal's default half-even rounding, also used by _calc_sums in the API.
function divide(n, d) {
  const sign = n < BigInt(0) ? BigInt(-1) : BigInt(1);
  const a = n * sign;
  const q = a / d;
  const r = a % d;
  return sign * (q + (r * BigInt(2) > d || (r * BigInt(2) === d && q % BigInt(2) !== BigInt(0)) ? BigInt(1) : BigInt(0)));
}

export function discountPercent(value) {
  const text = String(value ?? '').trim().replace(',', '.');
  if (!/^\d+(\.\d{1,4})?$/.test(text)) throw new Error('Introdu un procent între 0 și 100, cu maximum 4 zecimale.');
  const percent = fixed(text);
  if (percent <= BigInt(0) || percent > HUNDRED) throw new Error('Procentul trebuie să fie mai mare decât 0 și cel mult 100.');
  return percent;
}

export function isDiscountLine(line) {
  return line.bxpart?.cod === 'DISCOUNT' || /^DISCOUNT(?:\s*[•·]|$)/.test(line.bxpart_label || '');
}

export function canDiscountPrice(line) {
  return !line.environmental_fee && !isDiscountLine(line) && !line.storno_source_line_id && !line.bxstocin_id && Number(line.qty) > 0 && Number(line.price) > 0;
}

function recalc(line) {
  const base = fixed(line.qty, 6) * fixed(line.price, 6);
  const vat = fixed(line.vat_rate || 0);
  return { ...line,
    suma_fara_tva: Number(divide(base, BigInt(10000000000))) / 100,
    suma_tva: Number(divide(base * vat, BigInt(10000000000) * HUNDRED)) / 100,
    suma_total: Number(divide(base * (HUNDRED + vat), BigInt(10000000000) * HUNDRED)) / 100,
  };
}

export function validateDiscountPart(part) {
  if (part?.cod !== 'DISCOUNT') throw new Error('Nu există articolul DISCOUNT în nomenclator. Configurează-l ca serviciu prestat, cu un cont de vânzare.');
  if (Number(part.tip_grupa_produse) !== 3 || Number(part.grupa_produse?.tip_grupa) !== 3 || !part.grupa_produse?.contvanzare_id)
    throw new Error('Articolul DISCOUNT trebuie să fie serviciu prestat și să aibă contul de vânzare configurat în grupă.');
  return part;
}

export async function findDiscountPart(fetchJson) {
  let skip = 0;
  while (true) {
    const data = await fetchJson(`/bxpart?q=DISCOUNT&skip=${skip}&limit=100`);
    const items = data?.items || [];
    const exact = items.find(p => p.cod === 'DISCOUNT');
    if (exact) return validateDiscountPart(exact);
    skip += items.length;
    if (!items.length || skip >= Number(data.total ?? skip)) return validateDiscountPart(null);
  }
}

export function applyDiscount(lines, { mode, percent, selectedUid, part, makeUid }) {
  const rate = discountPercent(percent);
  if (!lines?.length) throw new Error('Factura nu are linii.');
  if (mode === 'all' || mode === 'line') {
    const eligible = lines.filter(l => canDiscountPrice(l) && (mode === 'all' || l.uid === selectedUid));
    if (!eligible.length) throw new Error('Alege o linie cu preț și cantitate pozitive, care nu este discount sau storno.');
    return lines.map(line => eligible.includes(line) ? recalc({ ...line,
      price: (Number(divide(fixed(line.price, 6) * (HUNDRED - rate), HUNDRED * BigInt(100))) / 10000).toFixed(4),
    }) : line);
  }
  if (mode !== 'invoice') throw new Error('Alege modul de acordare a discountului.');
  validateDiscountPart(part);
  const groups = new Map();
  for (const line of lines) {
    if (line.environmental_fee) continue;
    const vat = fixed(line.vat_rate || 0);
    const key = `${vat}:${line.cont_tva_id || ''}:${line.cont_client_id || ''}`;
    const group = groups.get(key) || { sample: line, base: BigInt(0) };
    // Existing negative discount lines reduce the remaining base on repeated use.
    group.base += divide(fixed(line.qty, 6) * fixed(line.price, 6), BigInt(10000000000));
    groups.set(key, group);
  }
  const additions = [];
  for (const { sample, base } of groups.values()) {
    if (base < BigInt(0)) throw new Error('Discountul pe factură necesită o bază pozitivă pentru fiecare cotă TVA.');
    const amount = divide(base * rate, HUNDRED);
    if (amount === BigInt(0)) continue;
    additions.push(recalc({
      uid: makeUid(), id: null, bxpart_id: part.id,
      bxpart_label: `${part.cod} • ${part.denumire}`, tip_grupa_produse: 3,
      descriere: `Discount ${String(percent).trim()}%`, qty: -1, price: (Number(amount) / 100).toFixed(4),
      bxgestiune_id: null, bxstocin_id: null, storno_source_line_id: null,
      alloc: [], serii: [], stock_err: '', stock_total: null, loadingAvail: false,
      vat_rate: sample.vat_rate || 0, vat_id: sample.vat_id ?? null, vat_label: sample.vat_label,
      cont_venit_id: part.grupa_produse.contvanzare_id,
      cont_tva_id: sample.cont_tva_id ?? null, cont_client_id: sample.cont_client_id ?? null,
    }));
  }
  if (!additions.length) throw new Error('Discountul calculat este zero. Verifică procentul și valorile facturii.');
  return [...lines, ...additions];
}

// Recalculate from editable values, not saved sums that can be stale after a discount.
export function invoiceTotals(lines = [], vatEnabled = true) {
  const sum = { base: BigInt(0), vat: BigInt(0), total: BigInt(0) };
  for (const line of lines || []) {
    const amount = recalc({ ...line, qty: line.qty || 0, price: line.price || 0,
      vat_rate: vatEnabled ? line.vat_rate || 0 : 0 });
    sum.base += fixed(amount.suma_fara_tva, 2);
    sum.vat += fixed(amount.suma_tva, 2);
    sum.total += fixed(amount.suma_total, 2);
  }
  return Object.fromEntries(Object.entries(sum).map(([key, cents]) => [key, Number(cents) / 100]));
}
